import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
    Box,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    Paper,
    Popover,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PersonSearchRoundedIcon from '@mui/icons-material/PersonSearchRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { SCUINoRecordComponent } from '../../../atomic/atoms';
import { SCUIBreadCrumb, SCUIGeneralDrawer } from '../../../atomic/molecules';
import { SCUIContent } from '../../../atomic/organisms';
import { OperatorCommand } from '../../../services/command';
import { OperatorQuery } from '../../../services/query';
import { PageUrl } from '../../../types/enums';
import ListUserDetailComponent from './Components/ListUserDetailComponent';
import { LUR } from './listUserRoles.classes';

import './ListUserRoles.scss';

export const ListUserRoles: FC<any> = () => {
    const { t } = useTranslation();
    const [value, setValue] = useState('');
    const [operators, setOperators] = useState<any>();
    const [loading, setLoading] = useState(false);
    const roleList: any = undefined;
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [drawerInfoOpen, setDrawerInfoOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<any>(null);
    const [refresh, setRefresh] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const history = useHistory();

    const handleDrawerOpen = (user: any) => {
        setDrawerInfoOpen(true);
        setSelectedUser(user);
    };

    const listDrawerOnClose = () => {
        setDrawerInfoOpen(false);
    };

    useEffect(() => {
        GetOperatorList();
    }, [refresh]);

    const refreshHandle = () => {
        setRefresh((prevRefreshing) => !prevRefreshing);
    };

    const GetOperatorList = async () => {
        try {
            if (value !== '' && value !== undefined && value !== null && value.length > 2) {
                setLoading(true);
                setOperators(undefined);

                const params: any = {};
                params.username = value;
                params.status = 'all';
                await OperatorQuery.GetOperatorFindByUserName(params).then((res) => {
                    setOperators(res.model.data);
                });
            }
        } catch (error) {
            console.log('Hata :>> ', error);
            setOperators(undefined);
        } finally {
            setLoading(false);
        }
    };

    const handleClosePopover = () => {
        setPopoverOpen(false);
        setAnchorEl(null);
    };

    const handleGotoRole = (_: React.ChangeEvent<HTMLInputElement>, role: any) => {
        history.push(PageUrl.editUser.url + '/?RoleId=' + role?.roleId, {
            roleId: role?.roleId,
            roleName: role?.roleNameKey,
        });
    };

    const handleUserActivityChange = (event: React.ChangeEvent<HTMLInputElement>, user: any) => {
        Swal.fire({
            text: t('authorizationManagement.listUser.surePassiveText'),
            cancelButtonText: t('general.cancel'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: t('general.update'),
        }).then((result: any) => {
            if (result.isConfirmed) {
                const params: any = {};
                params.applicationUserId = user?.applicationUserId;
                params.domainName = user.domainName;
                params.isActive = !event.target.checked;
                OperatorCommand.PutOperatorUpdate(params);
                const new_array = operators.map((element: any) =>
                    element.userRef === user?.userRef ? { ...element, isActive: !event.target.checked } : element
                );
                setOperators(new_array);
                Swal.fire(t('general.updated'), ' ', 'success');
            }
        });
    };

    const actionButtonSx = {
        borderRadius: '10px',
        transition: 'all 0.15s ease',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
        },
    };

    return (
        <SCUIContent className={LUR.page}>
            <Box className={LUR.detailDrawer}>
                <SCUIGeneralDrawer
                    open={drawerInfoOpen}
                    anchor="right"
                    width={'50rem'}
                    onClose={(_event: any, reason: any) => {
                        if (reason === 'backdropClick') {
                            setDrawerInfoOpen(false);
                        }
                    }}
                >
                    <ListUserDetailComponent
                        onClose={listDrawerOnClose}
                        selectedUser={selectedUser}
                        refresh={() => {
                            refreshHandle();
                        }}
                    />
                </SCUIGeneralDrawer>
            </Box>
            <Grid direction="row" className={LUR.root}>
                <Popover
                    className={LUR.rolesPopover}
                    open={popoverOpen}
                    onClose={handleClosePopover}
                    anchorEl={anchorEl}
                    slotProps={{
                        paper: {
                            sx: {
                                p: 2,
                                borderRadius: '14px',
                                boxShadow: '0 8px 32px rgba(15, 23, 42, 0.12)',
                            },
                        },
                    }}
                >
                    {roleList && roleList.length > 0 ? (
                        roleList.map((role: any, index: any) => (
                            <Chip
                                key={index}
                                className={LUR.rolesPopoverChip}
                                data-role-id={role.roleId}
                                data-role-name={role.roleNameKey}
                                label={role.roleNameKey}
                                onClick={(e: any) => handleGotoRole(e, role)}
                                sx={{ m: 0.5, fontWeight: 600 }}
                            />
                        ))
                    ) : (
                        <Chip className={LUR.rolesPopoverEmptyChip} label={t('general.notFound')} />
                    )}
                </Popover>

                <Box className={LUR.breadcrumb} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <SCUIBreadCrumb title={t('dashboard.listUserRoles')} componentLink={'/list-user-roles'} />
                </Box>

                <Paper className={LUR.searchPaper} elevation={0}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PersonSearchRoundedIcon sx={{ color: '#2563eb', fontSize: 24, flexShrink: 0 }} />
                        <TextField
                            className={LUR.inputSearch}
                            size="small"
                            fullWidth
                            placeholder={t('authorizationManagement.listUser.placeholderWarning')}
                            value={value}
                            disabled={loading}
                            onChange={(event) => setValue(event.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    GetOperatorList();
                                }
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '14px', backgroundColor: '#fff' } }}
                        />
                        <Tooltip title={t('general.search')}>
                            <IconButton
                                className={LUR.btnSearch}
                                aria-label="search"
                                disabled={loading}
                                onClick={() => GetOperatorList()}
                                sx={{
                                    border: '1px solid rgba(37, 99, 235, 0.25)',
                                    backgroundColor: 'rgba(37, 99, 235, 0.06)',
                                    color: '#2563eb',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(37, 99, 235, 0.14)',
                                        transform: 'scale(1.08)',
                                        boxShadow: '0 4px 14px rgba(37, 99, 235, 0.25)',
                                    },
                                }}
                            >
                                {loading ? <CircularProgress size={20} color="inherit" /> : <SearchRoundedIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Paper>

                {loading ? (
                    <Paper className={LUR.loading} elevation={0} sx={{ marginTop: 2, padding: 4, textAlign: 'center' }}>
                        <CircularProgress size={28} sx={{ mb: 1.5, color: '#2563eb' }} />
                        <Typography variant="body2" sx={{ color: '#6B7280', fontWeight: 500 }}>
                            {t('general.loading')}...
                        </Typography>
                    </Paper>
                ) : operators ? (
                    <TableContainer
                        component={Paper}
                        className={LUR.tableContainer}
                        elevation={0}
                        sx={{ marginTop: 2 }}
                    >
                        <Table sx={{ minWidth: 650 }} aria-label="simple table" className={LUR.table}>
                            <TableHead className={LUR.tableHead}>
                                <TableRow className={LUR.tableHeadRow}>
                                    <TableCell
                                        className={`${LUR.tableHeadCell} ${LUR.tableHeadCellUserName}`}
                                        data-column="user-name"
                                    >
                                        {t('general.userName')}
                                    </TableCell>
                                    <TableCell
                                        className={`${LUR.tableHeadCell} ${LUR.tableHeadCellOperatorId}`}
                                        data-column="operator-id"
                                    >
                                        {t('general.operatorId')}
                                    </TableCell>
                                    <TableCell
                                        className={`${LUR.tableHeadCell} ${LUR.tableHeadCellActive}`}
                                        data-column="active"
                                    >
                                        {t('general.active')}
                                    </TableCell>
                                    <TableCell
                                        className={`${LUR.tableHeadCell} ${LUR.tableHeadCellStoreCode}`}
                                        data-column="store-code"
                                        align="center"
                                    >
                                        {t('general.storeCode')}
                                    </TableCell>
                                    <TableCell
                                        className={`${LUR.tableHeadCell} ${LUR.tableHeadCellIsStoreEmployee}`}
                                        data-column="is-store-employee"
                                        align="center"
                                    >
                                        {t('general.isStoreEmployee')}
                                    </TableCell>
                                    <TableCell
                                        className={`${LUR.tableHeadCell} ${LUR.tableHeadCellDetail}`}
                                        data-column="detail"
                                        align="center"
                                    >
                                        {t('general.detail')}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody className={LUR.tableBody}>
                                {operators?.map((user: any) => (
                                    <TableRow
                                        key={user.userRef}
                                        className={LUR.tableBodyRow}
                                        data-user-ref={user.userRef}
                                        data-application-user-id={user.applicationUserId}
                                        data-username={user.username}
                                        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell
                                            component="th"
                                            scope="row"
                                            className={LUR.cellUserName}
                                            data-user-ref={user.userRef}
                                            data-username={user.username}
                                            sx={{ fontWeight: 600 }}
                                        >
                                            {user.username}
                                        </TableCell>
                                        <TableCell
                                            className={LUR.cellOperatorId}
                                            data-user-ref={user.userRef}
                                            data-operator-id={user.operatorId}
                                        >
                                            {user.operatorId}
                                        </TableCell>
                                        <TableCell
                                            className={LUR.cellActive}
                                            data-user-ref={user.userRef}
                                            data-is-active={user.isActive}
                                        >
                                            <Switch
                                                className={LUR.inputCellActiveSwitch}
                                                data-user-ref={user.userRef}
                                                data-is-active={user.isActive}
                                                checked={user.isActive}
                                                onChange={(e: any) => handleUserActivityChange(e, user)}
                                                color="primary"
                                            />
                                        </TableCell>
                                        <TableCell
                                            className={LUR.cellStoreCode}
                                            data-user-ref={user.userRef}
                                            data-store-code={user.storeCode || ''}
                                            align="center"
                                        >
                                            {user.storeCode || '-'}
                                        </TableCell>
                                        <TableCell
                                            className={LUR.cellIsStoreEmployee}
                                            data-user-ref={user.userRef}
                                            data-is-store-employee={
                                                user.isStoreEmployee === undefined ? '' : String(user.isStoreEmployee)
                                            }
                                            align="center"
                                        >
                                            {user.isStoreEmployee !== undefined ? (
                                                user.isStoreEmployee ? (
                                                    <Chip
                                                        icon={<CheckCircleOutlineIcon />}
                                                        label={t('general.yes')}
                                                        size="small"
                                                        className={LUR.cellIsStoreEmployeeYes}
                                                        sx={{
                                                            backgroundColor: '#DCFCE7',
                                                            color: '#15803D',
                                                            fontWeight: 600,
                                                            '& .MuiChip-icon': { color: '#15803D' },
                                                        }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        icon={<CancelOutlinedIcon />}
                                                        label={t('general.no')}
                                                        size="small"
                                                        className={LUR.cellIsStoreEmployeeNo}
                                                        sx={{
                                                            backgroundColor: '#FEE2E2',
                                                            color: '#DC2626',
                                                            fontWeight: 600,
                                                            '& .MuiChip-icon': { color: '#DC2626' },
                                                        }}
                                                    />
                                                )
                                            ) : (
                                                '-'
                                            )}
                                        </TableCell>
                                        <TableCell
                                            className={LUR.cellDetail}
                                            data-user-ref={user.userRef}
                                            align="center"
                                        >
                                            <Tooltip title={t('general.detail')}>
                                                <IconButton
                                                    className={LUR.btnDetail}
                                                    data-user-ref={user.userRef}
                                                    size="small"
                                                    onClick={() => handleDrawerOpen(user)}
                                                    sx={{
                                                        ...actionButtonSx,
                                                        color: '#2563EB',
                                                        border: '1px solid #BFDBFE',
                                                        backgroundColor: '#EFF6FF',
                                                    }}
                                                >
                                                    <InfoOutlinedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Box className={LUR.emptyState} sx={{ mt: 3 }}>
                        <SCUINoRecordComponent />
                    </Box>
                )}
            </Grid>
        </SCUIContent>
    );
};
